/**
 * API Configuration
 * Centralized API endpoints for the Banyan frontend.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// Auth endpoints are called SAME-ORIGIN in production: banyan_refresh and
// banyan_device are SameSite=Strict HttpOnly cookies, and the API's own host
// (*.on.aws) is cross-site from www.banyanai.co, so a browser neither stores
// nor returns them on a direct cross-site call. vercel.json proxies /auth/*
// to the backend so the cookies are first-party. Development has no proxy and
// keeps the direct URL (localhost is same-site across ports, so cookies work).
// REACT_APP_AUTH_API_URL overrides both for non-standard deployments.
const AUTH_API_BASE_URL =
  process.env.REACT_APP_AUTH_API_URL ??
  (process.env.NODE_ENV === 'production' ? '' : API_BASE_URL);

export const API_ENDPOINTS = {
  // Authentication endpoints
  auth: {
    login: `${AUTH_API_BASE_URL}/auth/login`,
    verifyLoginCode: `${AUTH_API_BASE_URL}/auth/verify-login-code`,
    verify: `${AUTH_API_BASE_URL}/auth/verify`,
    refresh: `${AUTH_API_BASE_URL}/auth/refresh`,
    logout: `${AUTH_API_BASE_URL}/auth/logout`,
    logoutAll: `${AUTH_API_BASE_URL}/auth/logout-all`,
    me: `${AUTH_API_BASE_URL}/auth/me`,
    changePassword: `${AUTH_API_BASE_URL}/auth/change-password`,
    forgotPassword: `${AUTH_API_BASE_URL}/auth/forgot-password`,
    verifyResetCode: `${AUTH_API_BASE_URL}/auth/verify-reset-code`,
    resetPassword: `${AUTH_API_BASE_URL}/auth/reset-password`,
    initiateAccountSetup: `${AUTH_API_BASE_URL}/auth/initiate-account-setup`,
    verifyEmailToken: `${AUTH_API_BASE_URL}/auth/verify-email-token`,
    setPassword: `${AUTH_API_BASE_URL}/auth/set-password`,
  },
  
  
  // Chat/AI endpoints
  chat: `${API_BASE_URL}/agent-answer`,
  chatStream: `${API_BASE_URL}/agent-answer/stream`,
  fieldInsightStream: `${API_BASE_URL}/field-insight/stream`,
  // A file the agent produced for one answer (workbook / memo / chart PNG).
  // Metadata rides on the answer's response_meta.artifacts; the bytes are
  // fetched here on demand, authenticated, and saved as a blob download.
  chatArtifact: (artifactId) => `${API_BASE_URL}/api/chat/artifacts/${artifactId}`,

  // Reports endpoints
  reports: {
    fetchData: `${API_BASE_URL}/api/reports/fetch-data`,
    // Job-and-poll generation. Used for GROUP reports only: they are the ones
    // whose serial per-member replay exceeds the ALB's 300s idle timeout, and
    // the job result is the same envelope fetchData returns.
    generateAsync: `${API_BASE_URL}/api/reports/generate-async`,
    job: (jobId) => `${API_BASE_URL}/api/reports/job/${jobId}`,
    // Stops an in-flight generation's Athena queries when the user closes the
    // report window before it finishes.
    fetchCancel: `${API_BASE_URL}/api/reports/fetch-cancel`,
    portfolios: (clientId) => `${API_BASE_URL}/api/reports/portfolios/${clientId}`,
    portfolioSummary: (clientId) => `${API_BASE_URL}/api/reports/portfolio/${clientId}/summary`,
    getCodenames: `${API_BASE_URL}/api/reports/codenames`,
    // Trailing-window gross trade volume per account (lazy: only fetched when the
    // Portfolios tab is sorted by recent trade volume).
    portfolioTradeVolume: `${API_BASE_URL}/api/reports/portfolio-trade-volume`,
    // Per-account latest + prior-snapshot market value (fetched fresh by the
    // Portfolios tab for the day-over-day change indicator; not subject to the
    // login preload's market-value timeout).
    portfolioMarketValues: `${API_BASE_URL}/api/reports/portfolio-market-values`,
  },
  // Saved report generation configurations ("Saved" rail on the Reports
  // page). Note: NOT under /api - mounted at /reports/templates directly
  // (backend/app/api/api_report_templates.py), matching the pre-existing
  // router prefix.
  reportTemplates: {
    list: (favoritesOnly) => `${API_BASE_URL}/reports/templates/${favoritesOnly ? '?favorites=true' : ''}`,
    create: `${API_BASE_URL}/reports/templates/`,
    update: (id) => `${API_BASE_URL}/reports/templates/${id}`,
    delete: (id) => `${API_BASE_URL}/reports/templates/${id}`,
  },
  manual: {
    variableRateBonds: `${API_BASE_URL}/api/manual/variable-rate-bonds`,
    vrbTimeline: (securityId) =>
      `${API_BASE_URL}/api/manual/variable-rate-bonds/${securityId}/timeline`,
    vrbEntries: (securityId) =>
      `${API_BASE_URL}/api/manual/variable-rate-bonds/${securityId}/entries`,
    vrbEntry: (securityId, entryId) =>
      `${API_BASE_URL}/api/manual/variable-rate-bonds/${securityId}/entries/${entryId}`,
    vrbClassification: (cusip) =>
      `${API_BASE_URL}/api/manual/variable-rate-bonds/classification/${cusip}`,
    groups: `${API_BASE_URL}/api/manual/groups`,
    group: (groupId) => `${API_BASE_URL}/api/manual/groups/${groupId}`,
    groupMembers: (groupId) => `${API_BASE_URL}/api/manual/groups/${groupId}/members`,
    groupMember: (groupId, clientId) => `${API_BASE_URL}/api/manual/groups/${groupId}/members/${clientId}`,
    accounts: `${API_BASE_URL}/api/manual/accounts`,
    account: (clientId) => `${API_BASE_URL}/api/manual/accounts/${clientId}`,
    pendingAccounts: `${API_BASE_URL}/api/manual/accounts/pending`,
    confirmPendingAccount: (acct) => `${API_BASE_URL}/api/manual/accounts/pending/${acct}/confirm`,
    dismissPendingAccount: (acct) => `${API_BASE_URL}/api/manual/accounts/pending/${acct}/dismiss`,
    dismissedAccounts: `${API_BASE_URL}/api/manual/accounts/dismissed`,
    firmBranding: `${API_BASE_URL}/api/manual/firm-branding`,
  },
  // Market Place: firm-wide held securities with prior-day MV, day-over-day
  // swing, and the firm's own last purchase date.
  marketplace: {
    securities: `${API_BASE_URL}/api/marketplace/securities`,
  },
  // Backend build info + data-lake freshness, for the System/Info page.
  system: {
    status: `${API_BASE_URL}/api/system/status`,
  },
  // Chat conversation persistence (server-backed Personal-Analyst history).
  conversations: {
    list: `${API_BASE_URL}/api/conversations/`,
    create: `${API_BASE_URL}/api/conversations/`,
    detail: (id) => `${API_BASE_URL}/api/conversations/${id}`,
    rename: (id) => `${API_BASE_URL}/api/conversations/${id}`,
    remove: (id) => `${API_BASE_URL}/api/conversations/${id}`,
    removeAll: `${API_BASE_URL}/api/conversations/`,
    feedback: (messageId) => `${API_BASE_URL}/api/conversations/messages/${messageId}/feedback`,
  },
};

export default API_BASE_URL;
